// GTI (Genetic Technological Innovations) form state and constants.
// Test data is fetched from FHIR ActivityDefinition resources via useTestCatalog.
// Panel compositions below are display metadata printed on GTI's requisition.
//
// PD-1529 — GTI publishes no order codes, so catalog codes are internal slugs
// (`GTI-…`) that are never shown in the UI or printed on the faxed requisition.
// GTI orders by test name.

export const GTI_QUESTIONNAIRE_ID = 'gti-blood-requisition'

// ---------------------------------------------------------------------------
// Panel compositions (display metadata — shows what each panel contains)
//
// Page 1 "BLOOD TEST PANEL" boxes, plus the page 2 "SUB PANELS" boxes that
// document what the composite individual tests (CBC W/ DIFF, CMP, BMP,
// TESTOSTERONE FREE) contain. Components are not separately orderable, and the
// form prints no code for them.
// ---------------------------------------------------------------------------

export const GTI_PANEL_COMPONENTS: Record<string, string[]> = {
  'GTI-LIPID-PANEL': ['CHOLESTEROL', 'HDL', 'VLDL', 'TRIGLYCERIDES', 'LDL'],
  'GTI-ANEMIA-PANEL': [
    'VITAMIN B12',
    'FOLATE',
    'TOTAL IRON BINDING CAPACITY (TIBC)',
    'IRON',
    'FERRITIN',
    'TRANSFERRIN SATURATION %',
    'UNSATURATED IRON BINDING CAPACITY (UIBC)',
  ],
  'GTI-THYROID-PANEL': [
    'PTH INTACT',
    'FREE T3',
    'TOTAL T3',
    'FREE T4',
    'TOTAL T4',
    'ANTI TPO',
    'ANTI TG',
  ],
  'GTI-BASIC-WELLNESS-PANEL': ['CMP', 'CBC W/ DIFF', 'LIPID PANEL'],
  'GTI-ELECTROLYTE-PANEL': ['SODIUM', 'POTASSIUM', 'CHLORIDE', 'CO2 (BICARBONATE)'],
  'GTI-HEPATIC-PANEL': [
    'TBIL (TOTAL BILIRUBIN)',
    'AST (ASPARTATE AMINOTRANSFERASE)',
    'ALT (ALANINE AMINOTRANSFERASE)',
    'ALP (ALKALINE PHOSPHATASE)',
    'TOTAL PROTEIN',
    'ALBUMIN',
  ],
  'GTI-RENAL-PANEL': [
    'POTASSIUM',
    'BUN (BLOOD UREA NITROGEN)',
    'SODIUM',
    'CO2 (BICARBONATE)',
    'PHOSPHORUS',
    'GLUCOSE',
    'CREATININE',
    'CHLORIDE',
    'CALCIUM',
    'ALBUMIN',
    'eGFR',
  ],
  'GTI-FEMALE-HORMONE-PANEL': [
    'PROGESTERONE',
    'TESTOSTERONE, FREE (CALCULATED)',
    'TESTOSTERONE, TOTAL',
    'ESTRADIOL',
    'DHEA-S',
    'PROLACTIN',
    'FSH',
    'LH',
  ],
  'GTI-MALE-HORMONE-PANEL': [
    'PROGESTERONE',
    'TESTOSTERONE, FREE (CALCULATED)',
    'TESTOSTERONE, TOTAL',
    'ESTRADIOL',
    'FSH',
    'LH',
  ],
  'GTI-CBC-W-DIFF': [
    'HEMOGLOBIN',
    'HEMATOCRIT',
    'RED BLOOD CELLS',
    'WHITE BLOOD CELLS',
    'PLATELETS',
  ],
  'GTI-CMP': [
    'ALBUMIN',
    'BILIRUBIN, TOTAL',
    'CALCIUM, TOTAL',
    'CARBON DIOXIDE',
    'CHLORIDE',
    'CREATININE',
    'GLUCOSE',
    'eGFR',
    'ALP',
    'POTASSIUM',
    'PROTEIN, TOTAL',
    'SODIUM',
    'ALT',
    'AST',
    'BUN',
  ],
  'GTI-BMP': [
    'POTASSIUM',
    'CREATININE',
    'BUN (BLOOD UREA NITROGEN)',
    'CO2 (BICARBONATE)',
    'CHLORIDE',
    'SODIUM',
    'CALCIUM',
    'GLUCOSE',
    'eGFR',
  ],
  'GTI-TESTOSTERONE-FREE': [
    'TESTOSTERONE TOTAL',
    'ALBUMIN',
    'SEX HORMONE BINDING GLOBULIN',
    'TESTOSTERONE FREE (CALCULATED)',
  ],
}

/** Panels the requisition marks "(Fast Required)". */
export const GTI_FASTING_PANELS = [
  'GTI-LIPID-PANEL',
  'GTI-ANEMIA-PANEL',
  'GTI-BASIC-WELLNESS-PANEL',
] as const

/**
 * The page-2 "SUB PANELS" boxes, in the order and wording the form prints them.
 *
 * These boxes carry no checkbox of their own: each one documents a composite
 * test that is ordered from the INDIVIDUAL TESTS list. So a sub panel is shown
 * as its own row but selects the same catalog code as its individual test —
 * check "Comp Metabolic Panel" here or "CMP" there and it is one order either
 * way, which is exactly how GTI's form reads.
 */
export const GTI_SUB_PANELS = [
  { code: 'GTI-CBC-W-DIFF', label: 'CBC', orderedAs: 'CBC W/ DIFF' },
  { code: 'GTI-CMP', label: 'Comp Metabolic Panel', orderedAs: 'CMP' },
  { code: 'GTI-BMP', label: 'Basic Metabolic Panel', orderedAs: 'BMP' },
  {
    code: 'GTI-TESTOSTERONE-FREE',
    label: 'Testosterone Free (Calculated)',
    orderedAs: 'TESTOSTERONE, FREE (CALCULATED)',
  },
] as const

/** The group a sub panel belongs to, wherever tests are grouped for display. */
export const GTI_SUB_PANEL_CATEGORY = 'Sub Panels'

const SUB_PANEL_CODES = new Set<string>(GTI_SUB_PANELS.map((subPanel) => subPanel.code))

/**
 * Whether a catalog code is one of the four SUB PANELS composites.
 *
 * The catalog files them under `individual-test`, because that is where GTI's
 * form makes them orderable — but the form documents them in a separate SUB
 * PANELS block, so everything that groups tests for a human keeps them apart
 * rather than burying a CBC W/ DIFF among the plain chemistries.
 */
export function isGtiSubPanel(code: string): boolean {
  return SUB_PANEL_CODES.has(code)
}

/**
 * The test name GTI prints for a sub panel, independent of the catalog.
 *
 * The sub panels are listed from the constant above rather than from the
 * fetched catalog, so a failed catalog request must not leave the requisition
 * naming a test by its internal slug — GTI cannot process that.
 */
export function gtiSubPanelTestName(code: string): string | undefined {
  return GTI_SUB_PANELS.find((subPanel) => subPanel.code === code)?.orderedAs
}

/** GTI processes only CMP when both are ordered — stated on page 2 of the form. */
export const GTI_CMP_CODE = 'GTI-CMP'
export const GTI_BMP_CODE = 'GTI-BMP'

// ---------------------------------------------------------------------------
// Form State
// ---------------------------------------------------------------------------

export interface GtiFormState {
  // Patient Information (auto-populated)
  firstName: string
  middleInitial: string
  lastName: string
  sexAtBirth: string
  dateOfBirth: string
  patientId: string
  patientAddress: string
  patientApt: string
  patientCity: string
  patientState: string
  patientZip: string
  patientPhone: string
  patientEmail: string

  // Provider
  physicianFirstName: string
  physicianLastName: string
  physicianNpi: string

  // Diagnosis
  diagnosisText: string

  // Primary Insurance
  primaryCarrier: string
  primaryMemberId: string
  primaryGroup: string
  policyholderName: string
  relationship: string

  // Collection instructions for GTI's collector. The draw-time facts (date, time,
  // tube counts, collector name, mileage) are recorded by whoever draws — GTI's
  // collector or their partner's, never ours — so this form does not collect them.
  isFasting: boolean
  isStat: boolean
  isVenipuncture: boolean

  // Test Selection
  selectedTests: string[]

  // Signature
  signatureDate: string
  signatureDataUrl: string

  // Provider organization (auto-populated, hidden)
  organizationName: string
  orgPhone: string
  orgFax: string
  orgAddress: string
  orgCity: string
  orgState: string
  orgZip: string
  orderingProviderName: string
  orderingProviderNpi: string
}

export function createInitialGtiFormState(): GtiFormState {
  return {
    firstName: '',
    middleInitial: '',
    lastName: '',
    sexAtBirth: '',
    dateOfBirth: '',
    patientId: '',
    patientAddress: '',
    patientApt: '',
    patientCity: '',
    patientState: '',
    patientZip: '',
    patientPhone: '',
    patientEmail: '',

    physicianFirstName: '',
    physicianLastName: '',
    physicianNpi: '',

    diagnosisText: '',

    primaryCarrier: '',
    primaryMemberId: '',
    primaryGroup: '',
    policyholderName: '',
    relationship: '',

    isFasting: false,
    isStat: false,
    isVenipuncture: false,

    selectedTests: [],

    signatureDate: '',
    signatureDataUrl: '',

    organizationName: '',
    orgPhone: '',
    orgFax: '',
    orgAddress: '',
    orgCity: '',
    orgState: '',
    orgZip: '',
    orderingProviderName: '',
    orderingProviderNpi: '',
  }
}
